// A kitchen slip must be idempotent: one job = one slip, ever.
//
// printJob() has no guard of any kind, so the mobile-ingest dedupe was the ONLY
// thing standing between a re-ingested order and the kitchen cooking it twice.
// When that guard let four copies of one order through (see pos.mobile-v1), four
// slips followed and the kitchen made four sets of food.
//
// Backed by localStorage, not an in-memory Set, because the replay that caused
// that incident happens across a page RELOAD — an in-memory guard is empty at
// exactly the moment it is needed.

const PRINT_MARKER_PREFIX = '__job_printed:'

// Long enough to cover a business day (the jobchain itself is cleared by Close
// Shop), short enough that markers cannot accumulate forever.
export const PRINT_MARKER_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Claim the right to print `jobId`, exactly once.
 *
 * Returns false ONLY when we are certain this job was already printed. Every
 * uncertain case — no jobId to key on, storage blocked or unavailable — returns
 * true: a duplicate slip is a bad day, a silently missing one is a lost order.
 */
export function claimJobPrint(jobId: any, now: number = Date.now()): boolean {
  const key = String(jobId ?? '')
  if (!key || typeof window === 'undefined') return true
  try {
    const marker = `${PRINT_MARKER_PREFIX}${key}`
    const prev = window.localStorage.getItem(marker)
    if (prev && now - Number(prev) < PRINT_MARKER_TTL_MS) return false
    window.localStorage.setItem(marker, String(now))
    return true
  } catch (e) {
    return true
  }
}

/**
 * Drop expired markers so they don't pile up. Called at plugin init, which also
 * covers the reload that follows Close Shop. Only EXPIRED markers go — a marker
 * still inside its window has a job to protect.
 */
export function sweepStalePrintMarkers(now: number = Date.now()): number {
  if (typeof window === 'undefined') return 0
  try {
    const toRemove: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (!k || !k.startsWith(PRINT_MARKER_PREFIX)) continue
      const v = Number(window.localStorage.getItem(k))
      if (!v || now - v >= PRINT_MARKER_TTL_MS) toRemove.push(k)
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k))
    return toRemove.length
  } catch (e) {
    // localStorage may be unavailable/blocked — non-fatal.
    return 0
  }
}
